import React from 'react';

import type { TableDefinitions } from '@gitbook/doc-core';

import type { TableDefinitionColorKinds } from './types';

/**
 * We rotate through the following color kinds for columns
 */
const ALL_COLUMN_COLORS_KINDS: Array<TableDefinitionColorKinds> = [
    'info',
    'merge',
    'upgrade',
    'danger',
];

function computeTableDefinitionColors(
    definitions: TableDefinitions,
    definitionsOrder: Array<string>
) {
    const { definitionColorsKindsMap } = definitionsOrder.reduce(
        (acc, col) => {
            const columnDef = definitions[col];
            if (columnDef.type === 'rating' || columnDef.type === 'select') {
                /**
                 * Each column that can be assigned a color (rating and select columns)
                 * When a color kind is assigned, we rotate and move to the next color kind
                 */
                const colorKindToBeAssigned = ALL_COLUMN_COLORS_KINDS[acc.colorIndexToBeClaimed];
                acc.definitionColorsKindsMap[col] = colorKindToBeAssigned;
                // Using modulo so that it can go to the start of array when reached at the end
                acc.colorIndexToBeClaimed =
                    (acc.colorIndexToBeClaimed + 1) % ALL_COLUMN_COLORS_KINDS.length;
            }
            return acc;
        },
        { colorIndexToBeClaimed: 0, definitionColorsKindsMap: {} } as {
            colorIndexToBeClaimed: number;
            definitionColorsKindsMap: Partial<Record<string, TableDefinitionColorKinds>>;
        }
    );
    return definitionColorsKindsMap;
}

export function useComputeTableDefinitionColorKinds(
    definitions: TableDefinitions,
    /** Ordered list of the definition IDs */
    definitionsOrder: Array<string>
) {
    const definitionColorKinds = React.useMemo(() => {
        return computeTableDefinitionColors(definitions, definitionsOrder);
    }, [definitions]);

    return definitionColorKinds;
}
